import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import "./QuoteSideDrawer.css";

/** One portal root moves between the rail and a closed/open native dialog.
 * Neither responsive layout nor dismissal unmounts stateful auxiliary content.
 * This component belongs in WorkspaceLayout.actions, outside the hidden rail.
 */
export function QuoteSideDrawer({
  railTarget,
  inlineTarget,
  enabled,
  ownerKey,
  pane = "details",
  requestedOpen,
  requestId,
  onPaneChange,
  onRequestClose,
  returnFocus,
  children,
}: {
  railTarget: HTMLElement | null;
  inlineTarget?: HTMLElement | null;
  enabled: boolean;
  ownerKey: string;
  pane?: "details" | "ask_ai";
  requestedOpen?: boolean;
  requestId?: number;
  onPaneChange?: (pane: "details" | "ask_ai") => void;
  onRequestClose?: () => void;
  returnFocus?: () => HTMLElement | null;
  children: ReactNode;
}) {
  const titleId = useId();
  const dialogId = useId();
  const dialog = useRef<HTMLDialogElement>(null);
  const drawerTarget = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const ignoredCloses = useRef(0);
  const invalidRequest = useRef<number | undefined>(undefined);
  const openedOwner = useRef<{
    ownerKey: string;
    requestId?: number;
    origin: "details" | "ask_ai";
  } | null>(null);
  const nextOrigin = useRef<"details" | "ask_ai">("details");
  const latest = useRef({
    ownerKey,
    requestId,
    enabled,
    inlineTarget,
    requestedOpen,
    returnFocus,
    onRequestClose,
  });
  latest.current = {
    ownerKey,
    requestId,
    enabled,
    inlineTarget,
    requestedOpen,
    returnFocus,
    onRequestClose,
  };
  const [content] = useState(() => {
    const node = document.createElement("div");
    node.className = "qq-auxiliary-content";
    return node;
  });
  const [narrow, setNarrow] = useState(
    () => window.matchMedia?.("(max-width: 1200px)").matches ?? false,
  );
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  const open = enabled && !inlineTarget && narrow && openedFor === ownerKey;
  const restoreFocus = () => {
    const opened = openedOwner.current,
      current = latest.current;
    if (
      !opened ||
      opened.ownerKey !== current.ownerKey ||
      opened.requestId !== current.requestId ||
      !current.enabled ||
      current.inlineTarget
    )
      return;
    const external = current.returnFocus?.();
    if (
      current.returnFocus &&
      (!external?.isConnected || external.getClientRects().length === 0)
    )
      return;
    (opened.origin === "ask_ai"
      ? (external ?? trigger.current)
      : trigger.current
    )?.focus({ preventScroll: true });
  };
  const dismiss = () => {
    const opened = openedOwner.current,
      current = latest.current;
    if (
      !opened ||
      opened.ownerKey !== current.ownerKey ||
      opened.requestId !== current.requestId
    )
      return;
    setOpenedFor(null);
    if (opened.origin === "ask_ai") current.onRequestClose?.();
  };
  useLayoutEffect(() => {
    setOpenedFor(null);
  }, [ownerKey, enabled]);
  useLayoutEffect(() => {
    if (requestedOpen === false) {
      setOpenedFor(null);
      return;
    }
    if (
      !requestedOpen ||
      !enabled ||
      inlineTarget ||
      !narrow ||
      invalidRequest.current === requestId
    )
      return;
    const captured = { ownerKey, requestId };
    const frame = requestAnimationFrame(() => {
      const current = latest.current;
      if (
        captured.ownerKey !== current.ownerKey ||
        captured.requestId !== current.requestId ||
        !current.enabled ||
        current.inlineTarget ||
        !current.requestedOpen
      )
        return;
      nextOrigin.current = "ask_ai";
      setOpenedFor(captured.ownerKey);
    });
    return () => cancelAnimationFrame(frame);
  }, [ownerKey, requestId, requestedOpen, enabled, inlineTarget, narrow]);
  useLayoutEffect(() => {
    const media = window.matchMedia?.("(max-width: 1200px)");
    const change = () => {
      setNarrow(media?.matches ?? false);
      // A resize back to narrow must not resurrect an old modal session.
      setOpenedFor(null);
      invalidRequest.current = latest.current.requestId;
      latest.current.onRequestClose?.();
    };
    media?.addEventListener?.("change", change);
    return () => media?.removeEventListener?.("change", change);
  }, []);
  useLayoutEffect(() => {
    const node = dialog.current!;
    const active = content.contains(document.activeElement)
      ? (document.activeElement as HTMLElement)
      : null;
    const wasModal = node.open;
    if (node.open && !open) {
      ignoredCloses.current++;
      node.close();
      if (narrow && enabled && !inlineTarget) restoreFocus();
    }
    const target = inlineTarget ?? (narrow ? drawerTarget.current : railTarget);
    if (target && content.parentElement !== target) {
      target.appendChild(content);
      if (inlineTarget && active) {
        const disclosure = inlineTarget.closest("details");
        if (disclosure) disclosure.open = true;
      }
      if ((inlineTarget || !narrow) && active?.isConnected)
        active.focus({ preventScroll: true });
      else if (inlineTarget && wasModal)
        inlineTarget
          .closest("[aria-label=审批视图]")
          ?.querySelector<HTMLButtonElement>("button")
          ?.focus({ preventScroll: true });
    }
    if (open && !node.open) {
      openedOwner.current = { ownerKey, requestId, origin: nextOrigin.current };
      node.showModal();
      node.querySelector<HTMLButtonElement>("[data-drawer-close]")?.focus();
    }
  }, [
    content,
    railTarget,
    inlineTarget,
    narrow,
    open,
    enabled,
    ownerKey,
    requestId,
  ]);
  useLayoutEffect(() => {
    const node = dialog.current!;
    // The portal follows its React ancestry for synthetic events. Listen on the
    // physical dialog so Tab from the reparented auxiliary root is also trapped.
    const trap = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = [
        ...node.querySelectorAll<HTMLElement>(
          'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),summary,[tabindex]:not([tabindex="-1"])',
        ),
      ].filter(
        (node) =>
          node.getClientRects().length > 0 &&
          getComputedStyle(node).visibility !== "hidden",
      );
      const first = focusable[0],
        last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    node.addEventListener("keydown", trap);
    return () => node.removeEventListener("keydown", trap);
  }, []);
  useLayoutEffect(() => {
    const node = dialog.current;
    return () => {
      if (node?.open) node.close();
      content.remove();
    };
  }, [content]);
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="qq-btn qq-auxiliary-trigger"
        hidden={!enabled || !!inlineTarget}
        aria-label="更多资料"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dialogId}
        onClick={() => {
          nextOrigin.current = "details";
          onPaneChange?.("details");
          setOpenedFor(ownerKey);
        }}
      >
        更多 · 文件与记录
      </button>
      <dialog
        ref={dialog}
        id={dialogId}
        className="qq-side-drawer"
        aria-labelledby={titleId}
        onCancel={(event) => {
          event.preventDefault();
          event.stopPropagation();
          dismiss();
        }}
        onClose={() => {
          if (ignoredCloses.current > 0) {
            ignoredCloses.current--;
            return;
          }
          dismiss();
          if (narrow && enabled && !inlineTarget) restoreFocus();
        }}
      >
        <div className="qq-side-drawer-head">
          <h2 id={titleId}>
            {pane === "ask_ai" ? "报价问 AI" : "报价资料与记录"}
          </h2>
          <button
            type="button"
            className="qq-btn"
            data-drawer-close
            onClick={dismiss}
          >
            关闭
          </button>
        </div>
        <div ref={drawerTarget} className="qq-side-drawer-body" />
      </dialog>
      {createPortal(children, content)}
    </>
  );
}
